<template>
  <div
    class="modal fade show hebrew-picker-modal"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    aria-labelledby="hebrew-picker-title"
    @click.self="closeModal"
    @keydown.esc="closeModal"
  >
    <div class="modal-dialog hebrew-picker-dialog" role="document">
      <div class="modal-content hebrew-picker-content">
        <header class="hebrew-picker-header">
          <div class="hebrew-picker-heading">
            <span class="hebrew-picker-glyph" aria-hidden="true">
              <i class="pi pi-calendar"></i>
            </span>
            <div>
              <h5 id="hebrew-picker-title">Select a Hebrew date</h5>
              <p>Choose a date from the Jewish calendar.</p>
            </div>
          </div>
          <button type="button" class="hebrew-picker-close" aria-label="Close" @click="closeModal">
            <i class="pi pi-times"></i>
          </button>
        </header>

        <div class="hebrew-picker-body frankRuhl-font">
          <div class="hebrew-picker-toolbar">
            <button
              type="button"
              class="hebrew-picker-nav"
              aria-label="Previous Hebrew month"
              @click="changeMonth(-1)"
            >
              <i class="pi pi-chevron-left"></i>
            </button>

            <div class="hebrew-picker-selects" dir="rtl">
              <label>
                <span class="sr-only">Hebrew month</span>
                <select v-model="currentMonth" aria-label="Hebrew month" @change="updateCalendar">
                  <option v-for="month in months" :key="month.number" :value="month.number">
                    {{ month.name }}
                  </option>
                </select>
              </label>
              <label>
                <span class="sr-only">Hebrew year</span>
                <select v-model="currentYear" aria-label="Hebrew year" @change="updateCalendar">
                  <option v-for="year in generateYearRange" :key="year" :value="year">
                    {{ yearLabel(year) }}
                  </option>
                </select>
              </label>
            </div>

            <button
              type="button"
              class="hebrew-picker-nav"
              aria-label="Next Hebrew month"
              @click="changeMonth(1)"
            >
              <i class="pi pi-chevron-right"></i>
            </button>
          </div>

          <div class="hebrew-picker-weekdays" aria-hidden="true">
            <div v-for="name in dayNames" :key="name.long" class="hebrew-picker-weekday">
              <span class="hebrew-picker-weekday__long">{{ name.long }}</span>
              <span class="hebrew-picker-weekday__short">{{ name.short }}</span>
            </div>
          </div>

          <div class="hebrew-picker-grid" role="grid" aria-label="Hebrew calendar days">
            <div
              v-for="empty in firstDayOffset"
              :key="`empty-${empty}`"
              class="hebrew-picker-empty"
              aria-hidden="true"
            ></div>
            <button
              v-for="day in calendarDays"
              :key="day.key"
              type="button"
              class="hebrew-picker-day"
              :class="{
                'hebrew-picker-day--selected': isSelected(day.hDate),
                'hebrew-picker-day--today': isToday(day.hDate),
                'hebrew-picker-day--holiday': Boolean(day.holiday),
              }"
              :aria-label="dayAriaLabel(day)"
              :aria-selected="String(Boolean(isSelected(day.hDate)))"
              :title="day.holiday || null"
              role="gridcell"
              @click="selectDate(day.hDate)"
            >
              <span class="hebrew-picker-day__number" dir="rtl">{{ day.dayLabel }}</span>
              <span class="hebrew-picker-day__gregorian" dir="ltr">
                {{ day.gregorianLabel }}
              </span>
              <span v-if="day.holiday" class="hebrew-picker-day__holiday" dir="rtl">
                {{ day.holiday }}
              </span>
            </button>
          </div>
        </div>

        <footer class="hebrew-picker-footer">
          <span class="hebrew-picker-legend">
            <span class="hebrew-picker-legend__swatch" aria-hidden="true"></span>
            Jewish holiday
          </span>
          <button type="button" class="hebrew-picker-today" @click="goToToday">
            <i class="pi pi-calendar-plus" aria-hidden="true"></i>
            Today
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { HDate } from 'hebcal';
import { DAYS_IN_HEBREW } from '@/utils/const';
import { formatHebrewYear } from '@/utils/hebrew_year';
import { jewishHolidayLabel } from '@/utils/holiday_label';
import { isIsraelOrganization } from '@/utils/yizkor';
import { toWallClockDate } from '@/utils/wall_clock_date';

// How far the year dropdown reaches either side of the current Hebrew year.
// Records such as yahrzeiten are often decades old, so it goes back a century.
const YEARS_BACK = 100;
const YEARS_AHEAD = 19;
const GREGORIAN_DAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
});
const GREGORIAN_ARIA_FORMATTER = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
});

// hebcal numbers the months Nisan (1) through Adar. A plain year ends at Adar
// (12); a leap year splits it into Adar I (12) and Adar II (13). Listing all
// three at once handed HDate a month 14, which quietly rolled the date over
// into the following year, so the tail of the list follows the year on screen.
const MONTHS_BEFORE_ADAR = [
    { name: 'ניסן', number: 1 },
    { name: 'אייר', number: 2 },
    { name: 'סיון', number: 3 },
    { name: 'תמוז', number: 4 },
    { name: 'אב', number: 5 },
    { name: 'אלול', number: 6 },
    { name: 'תשרי', number: 7 },
    { name: 'חשון', number: 8 },
    { name: 'כסלו', number: 9 },
    { name: 'טבת', number: 10 },
    { name: 'שבט', number: 11 }
];
const ADAR_PLAIN = [{ name: 'אדר', number: 12 }];
const ADAR_LEAP = [
    { name: "אדר א", number: 12 },
    { name: "אדר ב", number: 13 }
];

export default {
    name: 'JewishDatePicker',
    props: {
        initialDate: {
            type: [String, Date],
            default: null,
        },
    },
    emits: ['closeDatePickerModal', 'selectDate'],
    data() {
        const initialGregorianDate = toWallClockDate(this.initialDate);
        const initialHebrewDate = initialGregorianDate
            ? new HDate(initialGregorianDate)
            : new HDate();
        return {
            DAYS_IN_HEBREW,
            selectedDate: initialGregorianDate ? initialHebrewDate : null,
            currentMonth: initialHebrewDate.getMonth(),
            currentYear: initialHebrewDate.getFullYear(),
            dayNames: [
                { long: 'ראשון', short: 'א׳' },
                { long: 'שני', short: 'ב׳' },
                { long: 'שלישי', short: 'ג׳' },
                { long: 'רביעי', short: 'ד׳' },
                { long: 'חמישי', short: 'ה׳' },
                { long: 'שישי', short: 'ו׳' },
                { long: 'שב״ק', short: 'ש׳' }
            ]
        };
    },
    computed: {
        organization() {
            return this.$store?.getters?.organization?.data ||
                this.$store?.getters?.account?.data?.organization ||
                null;
        },
        isIsraelCalendar() {
            return isIsraelOrganization(this.organization);
        },
        isLeapYear() {
            return new HDate(1, 1, this.currentYear).isLeapYear();
        },
        months() {
            return MONTHS_BEFORE_ADAR.concat(this.isLeapYear ? ADAR_LEAP : ADAR_PLAIN);
        },
        calendarDays() {
            const days = [];
            const date = new HDate(1, this.currentMonth, this.currentYear);
            const totalDays = date.daysInMonth();
            for (let i = 1; i <= totalDays; i++) {
                const hDate = new HDate(i, this.currentMonth, this.currentYear);
                const gregorianDate = hDate.greg();
                days.push({
                    key: `${this.currentYear}-${this.currentMonth}-${i}`,
                    hDate,
                    dayLabel: DAYS_IN_HEBREW[i - 1] || String(i),
                    gregorianLabel: GREGORIAN_DAY_FORMATTER.format(gregorianDate),
                    gregorianAriaLabel: GREGORIAN_ARIA_FORMATTER.format(gregorianDate),
                    holiday: this.holidayForDay(hDate)
                });
            }
            return days;
        },
        firstDayOffset() {
            const firstDay = new HDate(1, this.currentMonth, this.currentYear);
            return firstDay.getDay();
        },
        generateYearRange() {
            const thisYear = new HDate().getFullYear();
            const firstYear = thisYear - YEARS_BACK;
            const length = YEARS_BACK + YEARS_AHEAD + 1;
            return Array.from({ length }, (_, i) => firstYear + i);
        }
    },
    methods: {
        yearLabel(year) {
            return formatHebrewYear(year);
        },
        closeModal() {
            this.$emit('closeDatePickerModal')
        },
        updateCalendar() {
            this.currentMonth = parseInt(this.currentMonth);
            this.currentYear = parseInt(this.currentYear);
            // Adar II exists only in a leap year. Left selected in a plain year
            // it would empty the month dropdown while the grid showed Adar.
            if (!this.isLeapYear && this.currentMonth > 12) {
                this.currentMonth = 12;
            }
        },
        lastMonthOfYear(year) {
            return new HDate(1, 1, year).isLeapYear() ? 13 : 12;
        },
        changeMonth(direction) {
            let month = Number(this.currentMonth);
            let year = Number(this.currentYear);

            if (direction > 0) {
                if (month === 6) {
                    month = 7;
                    year += 1;
                } else if (month === this.lastMonthOfYear(year)) {
                    month = 1;
                } else {
                    month += 1;
                }
            } else if (month === 7) {
                month = 6;
                year -= 1;
            } else if (month === 1) {
                month = this.lastMonthOfYear(year);
            } else {
                month -= 1;
            }

            this.currentMonth = month;
            this.currentYear = year;
            this.updateCalendar();
        },
        goToToday() {
            const today = new HDate();
            this.currentMonth = today.getMonth();
            this.currentYear = today.getFullYear();
        },
        // Shared with the screen schedule's range calendar, so the two can
        // never disagree about what a day is.
        holidayForDay(day) {
            return jewishHolidayLabel(day.greg(), { inIsrael: this.isIsraelCalendar, hebrew: true });
        },
        dayAriaLabel(day) {
            const month = this.months.find((item) => item.number === Number(this.currentMonth));
            const date = `${day.dayLabel} ${month?.name || ''} ${this.yearLabel(this.currentYear)}`;
            const accessibleDate = `${date}, ${day.gregorianAriaLabel}`;
            return day.holiday ? `${accessibleDate}, ${day.holiday}` : accessibleDate;
        },
        selectDate(day) {
            this.selectedDate = day;
            this.$emit('selectDate', this.selectedDate)
            this.closeModal()
        },
        isSelected(day) {
            return this.selectedDate && this.selectedDate.toString() === day.toString();
        },
        isToday(day) {
            const today = new HDate();
            return today.toString() === day.toString();
        }
    }
};
</script>
<style scoped>
.hebrew-picker-modal {
  display: block;
  z-index: 1080;
  overflow-y: auto;
  padding: 14px;
  background: rgba(17, 24, 39, 0.45);
  backdrop-filter: blur(2px);
}

.hebrew-picker-dialog {
  width: 100%;
  max-width: 600px;
  margin: 4vh auto;
}

.hebrew-picker-content {
  max-height: 92vh;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl) !important;
  background: var(--color-bg-canvas) !important;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
}

.hebrew-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--color-divider);
}

.hebrew-picker-heading {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
}

.hebrew-picker-glyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-md);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.hebrew-picker-glyph .pi {
  font-size: 0.84rem;
}

.hebrew-picker-heading h5 {
  margin: 0 0 3px;
  color: var(--color-text-primary);
  font-size: 0.92rem;
  font-weight: 600;
}

.hebrew-picker-heading p {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.7rem;
}

.hebrew-picker-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-md);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}

.hebrew-picker-close:hover {
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.hebrew-picker-body {
  min-height: 0;
  overflow-y: auto;
  padding: 14px 16px 16px;
}

.hebrew-picker-toolbar {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) 36px;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.hebrew-picker-selects {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.hebrew-picker-selects label {
  margin: 0;
}

.hebrew-picker-selects select {
  min-width: 112px;
  height: 36px !important;
  padding: 0 10px !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-md) !important;
  background-color: var(--color-bg-canvas) !important;
  color: var(--color-text-primary);
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
}

.hebrew-picker-selects select:hover {
  border-color: var(--color-primary-border) !important;
}

.hebrew-picker-selects select:focus {
  border-color: var(--color-primary) !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14) !important;
}

.hebrew-picker-nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}

.hebrew-picker-nav:hover {
  border-color: var(--color-primary-border);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.hebrew-picker-nav .pi {
  font-size: 0.72rem;
}

.hebrew-picker-weekdays,
.hebrew-picker-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 5px;
}

.hebrew-picker-weekdays {
  margin-bottom: 5px;
}

.hebrew-picker-weekday {
  min-width: 0;
  padding: 0 2px;
  color: var(--color-text-muted);
  font-size: 0.64rem;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
}

.hebrew-picker-weekday__short {
  display: none;
}

.hebrew-picker-day,
.hebrew-picker-empty {
  min-width: 0;
  min-height: 56px;
  border-radius: 9px;
}

.hebrew-picker-day {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 5px 3px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-canvas);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: transform .12s ease, background-color .15s ease, border-color .15s ease,
    box-shadow .15s ease;
}

.hebrew-picker-day:hover:not(.hebrew-picker-day--selected) {
  z-index: 1;
  border-color: var(--color-primary-border);
  background: var(--color-bg-soft);
  transform: translateY(-1px);
  box-shadow: var(--shadow-xs);
}

.hebrew-picker-day:focus-visible,
.hebrew-picker-nav:focus-visible,
.hebrew-picker-close:focus-visible,
.hebrew-picker-today:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2) !important;
}

.hebrew-picker-day__number {
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1;
}

.hebrew-picker-day__gregorian {
  color: var(--color-text-muted);
  font-family: inherit;
  font-size: 0.58rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

.hebrew-picker-day--today {
  box-shadow: inset 0 0 0 1px var(--color-primary);
}

.hebrew-picker-day--today::after {
  content: '';
  position: absolute;
  top: 5px;
  right: 5px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--color-primary);
}

.hebrew-picker-day--holiday {
  border-color: #FDE68A;
  background: #FFFBEB;
}

.hebrew-picker-day--holiday:hover:not(.hebrew-picker-day--selected) {
  border-color: #F6C453;
  background: #FFF7D6;
}

.hebrew-picker-day__holiday {
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  color: #A16207;
  font-size: 0.53rem;
  font-weight: 600;
  line-height: 1.15;
  text-align: center;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.hebrew-picker-day--selected,
.hebrew-picker-day--selected:hover {
  border-color: var(--color-primary);
  background: var(--color-primary);
  color: var(--color-text-on-primary);
  transform: none;
  box-shadow: 0 5px 14px rgba(99, 102, 241, 0.22);
}

.hebrew-picker-day--selected .hebrew-picker-day__holiday {
  color: rgba(255, 255, 255, 0.86);
}

.hebrew-picker-day--selected .hebrew-picker-day__gregorian {
  color: rgba(255, 255, 255, 0.78);
}

.hebrew-picker-day--selected.hebrew-picker-day--today::after {
  background: #fff;
}

.hebrew-picker-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 16px;
  border-top: 1px solid var(--color-divider);
  background: var(--color-bg-soft);
}

.hebrew-picker-legend {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-muted);
  font-size: 0.65rem;
}

.hebrew-picker-legend__swatch {
  width: 8px;
  height: 8px;
  border: 1px solid #FDE68A;
  border-radius: 3px;
  background: #FFFBEB;
}

.hebrew-picker-today {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-canvas);
  color: var(--color-primary-hover);
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
}

.hebrew-picker-today:hover {
  border-color: var(--color-primary-border);
  background: var(--color-primary-soft);
}

.hebrew-picker-today .pi {
  font-size: 0.72rem;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 575.98px) {
  .hebrew-picker-modal {
    padding: 8px;
  }

  .hebrew-picker-dialog {
    margin: 8px auto;
  }

  .hebrew-picker-content {
    max-height: calc(100dvh - 16px);
  }

  .hebrew-picker-header {
    padding: 12px;
  }

  .hebrew-picker-heading p {
    display: none;
  }

  .hebrew-picker-body {
    padding: 12px;
  }

  .hebrew-picker-toolbar {
    grid-template-columns: 34px minmax(0, 1fr) 34px;
    gap: 6px;
    margin-bottom: 12px;
  }

  .hebrew-picker-nav {
    width: 34px;
    height: 36px;
  }

  .hebrew-picker-selects {
    gap: 6px;
  }

  .hebrew-picker-selects label {
    min-width: 0;
    flex: 1 1 0;
  }

  .hebrew-picker-selects select {
    width: 100%;
    min-width: 0;
    padding: 0 8px !important;
    font-size: 0.78rem;
  }

  .hebrew-picker-weekdays,
  .hebrew-picker-grid {
    gap: 3px;
  }

  .hebrew-picker-weekday__long {
    display: none;
  }

  .hebrew-picker-weekday__short {
    display: inline;
  }

  .hebrew-picker-day,
  .hebrew-picker-empty {
    min-height: 52px;
  }

  .hebrew-picker-day {
    gap: 2px;
    padding: 4px 2px;
  }

  .hebrew-picker-day__number {
    font-size: 0.74rem;
  }

  .hebrew-picker-day__gregorian {
    font-size: 0.52rem;
  }

  .hebrew-picker-day__holiday {
    font-size: 0.48rem;
  }

  .hebrew-picker-footer {
    padding: 8px 12px;
  }
}
</style>

Availability builds on top of reliability
